import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/* Variant presets: padding + gap + alignment bundled together */
const variants = {
  default: { padding: 'p-5 sm:p-8', gap: 'gap-5', align: '' },
  compact: { padding: 'p-5 sm:p-6', gap: 'gap-5', align: '' },
  centered: { padding: 'px-5 sm:px-8 py-10 sm:py-16', gap: 'gap-6 sm:gap-10', align: 'items-center' },
};

export default function ModalWrapper({
  children, onClose, maxWidth = 'modal', variant = 'default', padding, gap, position = 'center', className = '',
  /* allowOverflow=true → max-h / overflow-y-auto 제거. dropdown/popover 가 모달 영역을 넘어
     서 자유롭게 보여야 하는 케이스(예: DateTimePicker 캘린더) 전용. 컨텐츠가 화면 높이를
     넘기지 않는다고 확신할 때만 사용. */
  allowOverflow = false,
}) {
  const v = variants[variant] || variants.default;
  const resolvedPadding = padding !== undefined ? padding : v.padding;
  const resolvedGap = gap !== undefined ? gap : v.gap;
  const resolvedAlign = v.align;
  const isBottom = position === 'bottom';

  /* Body scroll lock - prevents background scroll when modal is open (iOS Safari compatible) */
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const maxWidthMap = {
    modal: 'max-w-modal',
    'modal-md': 'max-w-modal-md',
    'modal-lg': 'max-w-modal-lg',
  };

  /* position="bottom": always anchored to bottom, top-only rounded corners */
  const overlayClass = isBottom
    ? 'fixed inset-0 lg:left-60 bg-black/50 z-[60] flex items-end justify-center animate-overlay'
    : 'fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-5 animate-overlay';

  const overflowClass = allowOverflow ? 'overflow-visible' : 'max-h-[85vh] sm:max-h-[calc(100vh-40px)] overflow-y-auto';
  const panelClass = isBottom
    ? `bg-white rounded-t-[20px] w-full flex flex-col ${resolvedGap} ${resolvedPadding} ${resolvedAlign} ${allowOverflow ? 'overflow-visible' : 'max-h-[85vh] overflow-y-auto'} animate-modal-content ${className}`
    : `bg-white rounded-t-modal sm:rounded-modal w-full ${maxWidthMap[maxWidth] || maxWidth} flex flex-col ${resolvedGap} ${resolvedPadding} ${resolvedAlign} ${overflowClass} animate-modal-content ${className}`;

  return createPortal(
    <div className={overlayClass} onClick={onClose || undefined}>
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
}
